import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FullScreenStepper, FullScreenStepperContent } from '@popmenu/admin-ui';
import { useIntl } from 'react-intl';
import { useFormContext } from 'react-form';
import ConfirmationModal from '~/admin/shared/forms/ConfirmationModal';
import useSelfSignUpFormSteps from './hooks/useSelfSignUpFormSteps';
import { useSelfSignUpContext } from './hooks/useSelfSignUpContext';
import type { Steps } from './helpers/validateFormFields';
import type { SelfSignUpForm } from './types';
import { clearPersistedFormData, usePersistSelfSignUpForm } from './hooks/useSelfSignUpFormPersistence';
import { getPlanSteps } from './PlansStep';

const useMessages = () => {
  const intl = useIntl();

  return {
    almostFinished: intl.formatMessage({ defaultMessage: 'We\'re almost finished setting up your account. You can return later, your restaurant will be ready to pop when you come back!' }),
    cancelButtonText: intl.formatMessage({ defaultMessage: 'Return to the form' }),
    confirmationMessage: intl.formatMessage({ defaultMessage: 'Are you sure you want to leave the sign up process?' }),
    proceedButtonText: intl.formatMessage({ defaultMessage: 'Cancel sign up' }),
    title: intl.formatMessage({ defaultMessage: "We're almost done!" }),
  };
};

interface Props {
  onClose: () => void,
  onCancel: () => void,
  onFinish: () => void,
  initialStepIndex?: number,
}

type OpenedBy = 'close' | 'cancel' | undefined

const SelfSignUpFormStepper = ({ onClose, onFinish, onCancel, initialStepIndex = 0 }: Props) => {
  const [openedBy, setOpenedBy] = useState<OpenedBy>();
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
  const { values } = useFormContext<SelfSignUpForm>();

  usePersistSelfSignUpForm(currentStepIndex);

  const { handleReset, setValidationStep, handleCaptureLead } = useSelfSignUpContext();
  const steps = useSelfSignUpFormSteps();
  const messages = useMessages();

  const productPlan = useMemo(() => values.productPlan ?? 'starter', [values.productPlan]);
  const planSteps = useMemo(() => getPlanSteps(productPlan), [productPlan]);

  const handleConfirmLeave = useCallback(() => {
    clearPersistedFormData();
    handleReset();
    if (openedBy === 'close') onClose();
    else if (openedBy === 'cancel') onCancel();
    setOpenedBy(undefined);
  }, [openedBy, onClose, onCancel, handleReset, setOpenedBy]);

  const handleClose = useCallback(() => {
    setOpenedBy('close');
  }, [setOpenedBy]);

  const handleCancel = useCallback(() => {
    setOpenedBy('cancel');
  }, [setOpenedBy]);

  const handleFinish = useCallback(() => {
    clearPersistedFormData();
    handleReset();
    onFinish();
  }, [handleReset, onFinish]);

  const isConfirmationDialogOpen = !!openedBy;

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const message = messages.almostFinished;
      e.preventDefault();

      // eslint-disable-next-line @typescript-eslint/no-deprecated
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload, { capture: true });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload, { capture: true });
    };
  }, [messages.almostFinished]);

  return (
    <React.Fragment>
      <FullScreenStepper
        open
        onClose={handleClose}
        onFinish={handleFinish}
        onCancel={handleCancel}
        resetStepCursorOnClose={false}
        resetStepCursorOnCancel={false}
        steps={steps}
        defaultStepCursor={initialStepIndex + 1}
        onNext={(args) => {
          const nextStepIndex = args.stepIndex + 1;
          const nextStep = steps[nextStepIndex];
          if (nextStep) setValidationStep(nextStep.name as Steps);
          handleCaptureLead();
          setCurrentStepIndex(nextStepIndex);
        }}
        onPrevious={(args) => {
          const previousStepIndex = args.stepIndex - 1;
          const previousStep = steps[previousStepIndex];
          if (previousStep) setValidationStep(previousStep.name as Steps);
          setCurrentStepIndex(previousStepIndex);
        }}
      >
        {planSteps.steps.map(({ name, component: StepComponent }) => (
          <FullScreenStepperContent key={name} name={name} overflow="hidden" width={1} height="100%">
            <StepComponent />
          </FullScreenStepperContent>
        ))}
      </FullScreenStepper>
      <ConfirmationModal
        open={isConfirmationDialogOpen}
        onClose={() => setOpenedBy(undefined)}
        confirmationAction={handleConfirmLeave}
        formattedMessage={messages.confirmationMessage}
        cancelButtonText={messages.cancelButtonText}
        proceedButtonText={messages.proceedButtonText}
        severity="warning"
        title={messages.title}
      />
    </React.Fragment>
  );
};

export default SelfSignUpFormStepper;
